import { TransactionRole, Web3TransactionType, AccountOwnerType, TransactionStatus } from '@explorins/pers-shared';
import { b as buildPaginationParams } from './pagination-utils-9vQ-Npkr.js';

/**
 * Platform-Agnostic Transaction API Client (UPDATED FOR NEW RESTful ENDPOINTS)
 *
 * Handles transaction operations using the PERS backend.
 * Uses @explorins/pers-shared DTOs for consistency with backend.
 *
 * MIGRATION NOTES:
 * - All endpoints changed from /transaction to /transactions
 * - Role-based paths removed (no more /auth, /admin, /business in URLs)
 * - New RESTful resource-based structure
 * - Added new client-side transaction flow methods
 * - Enhanced admin query capabilities
 */
class TransactionApi {
    constructor(apiClient) {
        this.apiClient = apiClient;
        this.basePath = '/transactions';
    }
    /**
     * Get transaction by ID (public endpoint)
     *
     * UPDATED: /transaction/{id} → /transactions/{id}
     * UPDATED: Added support for include parameter to enrich with sender/recipient/business entities
     *
     * @param transactionId - Transaction ID
     * @param include - Optional relations to include (sender, recipient, business)
     */
    async getTransactionById(transactionId, include) {
        let url = `${this.basePath}/${transactionId}`;
        if (include && include.length > 0) {
            const params = new URLSearchParams();
            include.forEach(relation => params.append('include', relation));
            url += `?${params.toString()}`;
        }
        return this.apiClient.get(url);
    }
    /**
     * Unique method to create a transaction
     * @param request
     * @returns
     */
    async createTransaction(request) {
        return this.apiClient.post(`${this.basePath}`, request);
        // return this.apiClient.post<TransactionDTO>(`${this.basePath}/system`, request);
    }
    // ==========================================
    // AUTHENTICATED USER OPERATIONS
    // ==========================================
    /**
     * Helper to build query params from TransactionQueryParams
     * Handles all filter options and include parameter
     */
    buildTransactionQueryParams(options) {
        const params = buildPaginationParams(options);
        if (!options)
            return params;
        // Search and identification filters
        if (options.search)
            params.append('search', options.search);
        if (options.participantId)
            params.append('participantId', options.participantId);
        if (options.participantAddress)
            params.append('participantAddress', options.participantAddress);
        // Business and process filters
        if (options.engagedBusinessId)
            params.append('engagedBusinessId', options.engagedBusinessId);
        if (options.businessScopeId)
            params.append('businessScopeId', options.businessScopeId);
        if (options.triggerProcessId)
            params.append('triggerProcessId', options.triggerProcessId);
        // Token filters
        if (options.tokenAddress)
            params.append('tokenAddress', options.tokenAddress);
        if (options.tokenType) {
            if (Array.isArray(options.tokenType)) {
                options.tokenType.forEach(type => params.append('tokenType', type));
            }
            else {
                params.append('tokenType', options.tokenType);
            }
        }
        // Transaction classification filters (arrays or single values)
        if (options.role) {
            if (Array.isArray(options.role)) {
                options.role.forEach(role => params.append('role', role));
            }
            else {
                params.append('role', options.role);
            }
        }
        if (options.status) {
            if (Array.isArray(options.status)) {
                options.status.forEach(status => params.append('status', status));
            }
            else {
                params.append('status', options.status);
            }
        }
        if (options.type) {
            if (Array.isArray(options.type)) {
                options.type.forEach(type => params.append('type', type));
            }
            else {
                params.append('type', options.type);
            }
        }
        // Date range filters
        if (options.dateFrom)
            params.append('dateFrom', options.dateFrom);
        if (options.dateTo)
            params.append('dateTo', options.dateTo);
        // Include relations for entity enrichment
        if (options.include && options.include.length > 0) {
            options.include.forEach(relation => params.append('include', relation));
        }
        return params;
    }
    /**
     * AUTH: Get user transaction history with role-based filtering and pagination
     *
     * UPDATED: Uses consolidated /transactions/me endpoint with all filter options
     * UPDATED: Added support for include parameter to enrich with sender/recipient/business entities
     * UPDATED: Now supports all TransactionFiltersDTO filters (triggerProcessId, status, type, etc.)
     *
     * @param options - Pagination, filter, and include options
     * @returns Paginated list of user's transactions
     *
     * @example
     * ```typescript
     * // Get first page of all transactions
     * const page1 = await transactionApi.getUserTransactionHistory();
     *
     * // Filter by role and status
     * const completed = await transactionApi.getUserTransactionHistory({
     *   role: TransactionRole.SENDER,
     *   status: TransactionStatus.COMPLETED,
     *   page: 1,
     *   limit: 50
     * });
     *
     * // Filter by trigger process (e.g., from campaign claim or redemption)
     * const claimTransactions = await transactionApi.getUserTransactionHistory({
     *   triggerProcessId: 'claim-abc123',
     *   include: ['sender', 'recipient', 'business']
     * });
     *
     * // Access included entities
     * claimTransactions.data.forEach(tx => {
     *   if (tx.included?.engagedBusiness) {
     *     console.log('Business:', tx.included.engagedBusiness.displayName);
     *   }
     * });
     * ```
     */
    async getUserTransactionHistory(options) {
        const params = this.buildTransactionQueryParams(options);
        return this.apiClient.get(`${this.basePath}/me?${params.toString()}`);
    }
    /**
     * AUTH: Prepare existing transaction for client-side signing
     *
     * NEW ENDPOINT: GET /transactions/{id}/prepare
     */
    async prepareExistingTransaction(transactionId) {
        return this.apiClient.get(`${this.basePath}/${transactionId}/prepare`);
    }
    /**
     * AUTH: Submit client-side signed transaction
     *
     * NEW ENDPOINT: POST /transactions/submit
     */
    async submitSignedTransaction(signedTxData) {
        return this.apiClient.post(`${this.basePath}/submit`, signedTxData);
    }
    // ==========================================
    // BUSINESS OPERATIONS
    // ==========================================
    /**
     * BUSINESS: Create business transaction
     *
     * UPDATED: /transaction/business/transaction → /transactions/business
     */
    /* async createBusinessTransaction(request: TransactionRequestDTO): Promise<TransactionRequestResponseDTO> {
      return this.apiClient.post<TransactionRequestResponseDTO>(`${this.basePath}`, request);
    } */
    // ==========================================
    // ADMIN OPERATIONS
    // ==========================================
    /**
     * ADMIN: Create admin transaction
     *
     * UPDATED: /transaction/admin/transaction → /transactions/admin
     */
    /* async createAdminTransaction(request: TransactionRequestDTO): Promise<TransactionRequestResponseDTO> {
      // return this.apiClient.post<TransactionRequestResponseDTO>(`${this.basePath}`, request);
      return this.apiClient.post<TransactionDTO>(`${this.basePath}/system`, request);
    } */
    /**
     * AUTH: Prepare client signed transaction (Create new transaction for signing)
     *
     * UPDATED: /transaction/auth/prepare-signing → /transactions (POST)
     */
    async prepareClientSignedTransaction(request) {
        return this.apiClient.post(`${this.basePath}`, request);
    }
    /**
     * ADMIN: Get paginated transactions with filtering and sorting
     *
     * UPDATED: /transaction/admin → /transactions (same endpoint, better structure)
     *
     * @param options - Pagination request with filters and optional include relations
     */
    async getPaginatedTransactions(options) {
        const queryString = this.buildQueryParams(options).toString();
        const fullUrl = `${this.basePath}?${queryString}`;
        return this.apiClient.get(fullUrl);
    }
    /**
     * ADMIN: Export transactions to CSV
     *
     * Returns a CSV file with transaction data for the tenant.
     * Supports date range filtering.
     *
     * @param options - Export options (dateFrom, dateTo)
     * @returns CSV blob
     */
    async exportCSV(options) {
        const params = new URLSearchParams();
        if (options?.dateFrom)
            params.append('dateFrom', options.dateFrom);
        if (options?.dateTo)
            params.append('dateTo', options.dateTo);
        const queryString = params.toString();
        const url = `${this.basePath}/export/csv${queryString ? `?${queryString}` : ''}`;
        return this.apiClient.get(url, 'blob');
    }
    /**
     * @deprecated Use exportCSV() instead
     */
    async exportTransactionsCSV() {
        return this.exportCSV();
    }
    // ==========================================
    // NEW ADMIN QUERY METHODS
    // ==========================================
    /**
     * ADMIN: Query transactions by sender
     *
     * NEW ENDPOINT: POST /transactions/query-sender
     */
    /**
   * Query transactions by sender using unified endpoint
   */
    async queryTransactionsBySender(accountSelector) {
        // Build query parameters safely
        const queryParams = {};
        if (accountSelector.accountId) {
            queryParams['participantId'] = accountSelector.accountId;
        }
        queryParams['role'] = TransactionRole.SENDER.toString();
        const params = new URLSearchParams(queryParams);
        return this.apiClient.get(`${this.basePath}?${params.toString()}`).then(response => response); // Extract items from paginated response
    }
    /**
     * Query transactions by recipient using unified endpoint
     */
    async queryTransactionsByRecipient(accountSelector) {
        // Build query parameters safely
        const queryParams = {};
        if (accountSelector.accountId) {
            queryParams['participantId'] = accountSelector.accountId;
        }
        queryParams['role'] = TransactionRole.RECIPIENT.toString();
        const params = new URLSearchParams(queryParams);
        return this.apiClient.get(`${this.basePath}?${params.toString()}`).then(response => response); // Extract items from paginated response
    }
    /**
     * ADMIN: Get transaction analytics
     *
     * NEW ENDPOINT: POST /transactions/analytics
     */
    async getTransactionAnalytics(analyticsRequest) {
        return this.apiClient.post(`${this.basePath}/analytics`, analyticsRequest);
    }
    /**
     * Helper to convert DTO object to URLSearchParams
     * Handles nested 'filters' object, arrays, and include parameter correctly.
     */
    buildQueryParams(params) {
        const query = new URLSearchParams();
        // 1. Handle Root Pagination Fields
        if (params.page !== undefined)
            query.append('page', params.page.toString());
        if (params.limit !== undefined)
            query.append('limit', params.limit.toString());
        if (params.sortBy)
            query.append('sortBy', params.sortBy);
        if (params.sortOrder)
            query.append('sortOrder', params.sortOrder);
        // 2. Handle Include Relations (root level)
        if (params.include && params.include.length > 0) {
            params.include.forEach((relation) => query.append('include', relation));
        }
        // 3. Handle Nested Filters
        if (params.filters) {
            Object.entries(params.filters).forEach(([key, value]) => {
                // Skip undefined/null values
                if (value === undefined || value === null || value === '')
                    return;
                if (Array.isArray(value)) {
                    // Handle Arrays: NestJS expects 'status=A&status=B'
                    value.forEach((item) => query.append(key, String(item)));
                }
                else {
                    // Handle Single Values: 'search=0x123'
                    // NOTE: Backend Controller expects flat query params for filters
                    // e.g. ?search=...&status=... NOT ?filters[search]=...
                    // This mapping flattens 'filters.search' -> 'search' to match @Query('search') in Controller
                    const stringValue = (value instanceof Date) ? value.toISOString() : String(value);
                    query.append(key, stringValue);
                }
            });
        }
        return query;
    }
}

/**
 * Platform-Agnostic Transaction Service
 *
 * Contains transaction business logic and operations that work across platforms.
 * No framework dependencies - pure TypeScript business logic.
 *
 * Focuses only on actual backend capabilities.
 */
class TransactionService {
    constructor(transactionApi) {
        this.transactionApi = transactionApi;
    }
    /**
     * Get transaction by ID
     *
     * @param transactionId - Transaction ID
     * @param include - Optional relations to include (sender, recipient, business)
     */
    async getTransactionById(transactionId, include) {
        return this.transactionApi.getTransactionById(transactionId, include);
    }
    // ==========================================
    // AUTHENTICATED OPERATIONS
    // ==========================================
    /**
     * AUTH: Create authenticated transaction
     */
    /* async createAuthTransaction(request: TransactionRequestDTO): Promise<TransactionRequestResponseDTO> {
      return this.transactionApi.createAuthTransaction(request);
    } */
    async createTransaction(request) {
        return this.transactionApi.createTransaction(request);
    }
    async submitSignedTransaction(signedTxData) {
        return this.transactionApi.submitSignedTransaction(signedTxData);
    }
    /**
     * AUTH: Get user transaction history with comprehensive filtering
     *
     * @param options - Query options including filters, pagination, and include relations
     * @returns Paginated transaction history
     */
    async getUserTransactionHistory(options) {
        return this.transactionApi.getUserTransactionHistory(options);
    }
    /**
     * AUTH: Prepare existing transaction for client-side signing
     */
    async prepareExistingTransaction(transactionId) {
        return this.transactionApi.prepareExistingTransaction(transactionId);
    }
    /**
     * AUTH: Prepare client signed transaction
     */
    async prepareClientSignedTransaction(request) {
        return this.transactionApi.prepareClientSignedTransaction(request);
    }
    // ==========================================
    // BUSINESS OPERATIONS
    // ==========================================
    /**
     * BUSINESS: Create business transaction
     */
    /* async createBusinessTransaction(request: TransactionRequestDTO): Promise<TransactionRequestResponseDTO> {
      return this.transactionApi.createBusinessTransaction(request);
    } */
    // ==========================================
    // ADMIN OPERATIONS
    // ==========================================
    /**
     * ADMIN: Create admin transaction
     */
    /* async createAdminTransaction(request: TransactionRequestDTO): Promise<TransactionRequestResponseDTO> {
      return this.transactionApi.createAdminTransaction(request);
    } */
    /**
     * ADMIN: Get paginated transactions with filtering and sorting
     */
    async getPaginatedTransactions(options) {
        return this.transactionApi.getPaginatedTransactions(options);
    }
    /**
     * ADMIN: Export transactions to CSV
     */
    async exportTransactionsCSV(options) {
        return this.transactionApi.exportCSV(options);
    }
    // ==========================================
    // QUERY & ANALYTICS OPERATIONS
    // ==========================================
    /**
     * Query transactions by sender
     */
    async queryTransactionsBySender(accountSelector) {
        return this.transactionApi.queryTransactionsBySender(accountSelector);
    }
    /**
     * Query transactions by recipient
     */
    async queryTransactionsByRecipient(accountSelector) {
        return this.transactionApi.queryTransactionsByRecipient(accountSelector);
    }
    /**
     * ADMIN: Get transaction analytics
     */
    async getTransactionAnalytics(analyticsRequest) {
        return this.transactionApi.getTransactionAnalytics(analyticsRequest);
    }
}

// ============================================================================
// TRANSACTION RESPONSE HELPERS
// ============================================================================
/**
 * 🔍 Check if a transaction response requires external wallet signing
 *
 * Returns true when the user needs to sign the transaction externally
 * (e.g., via WebAuthn signer app) before it can be submitted.
 *
 * @example
 * ```typescript
 * const response = await sdk.transactions.createTransaction(request);
 * if (needsExternalSigning(response)) {
 *   const signingUrl = getSigningUrl(response);
 *   // Show "Click to Sign" button with signingUrl
 * }
 * ```
 */
function needsExternalSigning(response) {
    if (!response)
        return false;
    return response.walletSigningStatus === 'WALLET_MISSING_SIGNING_ACCOUNT' ||
        response.transactionStatus === TransactionStatus.PENDING_SIGNATURE;
}
/**
 * 🔍 Check if a transaction response is signed and ready for submission
 *
 * Returns true when the user has signed the transaction and it's waiting
 * to be submitted to the blockchain (typically by a business in POS flows).
 *
 * @example
 * ```typescript
 * const response = await sdk.transactions.getTransaction(transactionId);
 * if (needsSubmission(response)) {
 *   // Transaction is signed, ready to submit
 *   await sdk.transactions.submitSignedTransaction({ ... });
 * }
 * ```
 */
function needsSubmission(response) {
    if (!response)
        return false;
    return response.walletSigningStatus === 'WALLET_HAS_SIGNED' &&
        response.transactionStatus === TransactionStatus.PENDING_SUBMISSION;
}
/**
 * 🔗 Get the signing URL from a transaction response
 *
 * Returns the URL where the user should be redirected to sign the transaction.
 * Optionally appends `signOnly=true` for POS flows where the signer app
 * should skip the confirmation UI.
 *
 * @param response - Transaction response from API
 * @param options - Optional configuration
 * @param options.signOnly - If true, appends signOnly param for POS flows (default: false)
 * @returns Signing URL or null if not available
 *
 * @example
 * ```typescript
 * const signingUrl = getSigningUrl(response, { signOnly: isPOSTransaction });
 * if (signingUrl) {
 *   window.open(signingUrl, '_blank');
 * }
 * ```
 */
function getSigningUrl(response, options) {
    if (!response?.actionable?.actionUrl)
        return null;
    const baseUrl = response.actionable.actionUrl;
    // For POS transactions, append signOnly to skip confirmation in signer app
    if (options?.signOnly) {
        return baseUrl + '&signOnly=true';
    }
    return baseUrl;
}
// ============================================================================
// CLIENT TRANSACTION TYPES
// ============================================================================
/**
 * Client-side transaction types extending backend Web3TransactionType
 * Includes client-specific flows like pending submissions (POS flow)
 *
 * Pattern follows loyalty app: extend Web3TransactionType with client-side types
 */
const ClientTransactionType = {
    ...Web3TransactionType,
    /** Transaction signed by user, pending business submission (POS QR flow) */
    PENDING_SUBMISSION: 'PENDING_SUBMISSION',
};
/**
 * Helper to extract deadline from transaction signing data
 * Works with EIP-712 typed data structure from counterfactual wallet
 *
 * @param signingData - The signing data from TransactionSigningResult
 * @returns Deadline timestamp in seconds, or null if not found
 */
function extractDeadlineFromSigningData(signingData) {
    if (!signingData || typeof signingData !== 'object')
        return null;
    const data = signingData;
    // Check typedData.message.deadline (EIP-712 standard location)
    if (data.typedData?.message?.deadline) {
        return data.typedData.message.deadline;
    }
    // Check metadata.deadline (alternative location)
    if (data.metadata?.deadline) {
        return data.metadata.deadline;
    }
    return null;
}
/**
 * Build pending transaction data for transfer to business
 * Returns properly typed object ready for serialization (QR code, NFC, deep link, etc.)
 *
 * Pattern from loyalty app: scan.component.ts
 *
 * @param transactionId - Transaction ID from signing result
 * @param signature - User's signature from WebAuthn
 * @param transactionFormat - Format (e.g., 'EIP-712')
 * @returns Typed object with txType set to PENDING_SUBMISSION
 */
function buildPendingTransactionData(transactionId, signature, transactionFormat = 'EIP-712') {
    return {
        transactionId,
        signature,
        transactionFormat,
        txType: ClientTransactionType.PENDING_SUBMISSION,
    };
}
// ============================================================================
// SHARED HELPERS (internal)
// ============================================================================
function buildTokenSelector(data) {
    return {
        contractAddress: data.contractAddress,
        chainId: data.chainId,
        // Only include contractTokenId if it's not null (for NFTs)
        ...(data.contractTokenId !== null && data.contractTokenId !== undefined && {
            contractTokenId: String(data.contractTokenId)
        })
    };
}
function buildAccountSelector(data) {
    return {
        // ✅ Always prefer accountId and accountType when available
        accountId: data.accountId,
        accountType: data.accountType,
        // ⚠️ Only use accountAddress as fallback if no account info available
        ...((!data.accountId && data.accountAddress) && {
            accountAddress: data.accountAddress
        })
    };
}
// ============================================================================
// PUBLIC FACTORY FUNCTIONS
// ============================================================================
/**
 * 🎯 Build a MINT transaction request (admin/business endpoint)
 * Creates tokens and assigns them to a recipient account
 *
 * @example Basic Mint
 * ```typescript
 * const request = buildMintRequest({
 *   amount: 100,
 *   contractAddress: '0x...',
 *   chainId: 137,
 *   recipientAccountId: 'user-123',
 *   recipientAccountType: AccountOwnerType.USER,
 *   engagedBusinessId: 'business-456' // optional: for tracking which business triggered the mint
 * });
 * ```
 *
 * @example ERC721 Mint with Dynamic Context
 * ```typescript
 * const request = buildMintRequest({
 *   amount: 1,
 *   contractAddress: '0x...',
 *   contractTokenId: '123', // TokenMetadata ID
 *   chainId: 137,
 *   recipientAccountId: 'user-123',
 *   recipientAccountType: AccountOwnerType.USER,
 *   context: {
 *     // Validity control
 *     validityDate: '2026-04-15T14:00:00Z',
 *     validityEndDate: '2026-04-20T11:00:00Z',
 *     // AI prompt placeholders (available as {{context.xxx}})
 *     guestName: 'John Doe',
 *     roomNumber: '305',
 *     awardReason: 'Employee of the Month'
 *   }
 * });
 * ```
 */
function buildMintRequest(data) {
    return {
        web3TransactionType: Web3TransactionType.MINT,
        amount: Math.round(data.amount),
        token: buildTokenSelector(data),
        ...(data.recipientAccountId || data.recipientAccountType || data.recipientAccountAddress) && {
            recipient: buildAccountSelector({
                accountId: data.recipientAccountId,
                accountType: data.recipientAccountType,
                accountAddress: data.recipientAccountAddress
            })
        },
        ...(data.engagedBusinessId && { engagedBusinessId: data.engagedBusinessId }),
        ...(data.context && { context: data.context })
    };
}
/**
 * 🎯 Build a BURN transaction request
 * For auth endpoint: sender is handled automatically by backend
 * For admin endpoint: provide senderAccountId/Type to burn on behalf of user
 *
 * @example
 * ```typescript
 * // Auth endpoint (sender auto-resolved)
 * const request = buildBurnRequest({
 *   amount: 50,
 *   contractAddress: '0x...',
 *   chainId: 137
 * });
 *
 * // Admin endpoint (explicit sender)
 * const request = buildBurnRequest({
 *   amount: 50,
 *   contractAddress: '0x...',
 *   chainId: 137,
 *   senderAccountId: 'user-123',
 *   senderAccountType: AccountOwnerType.USER
 * });
 *
 * // POS burn (business submits on behalf of user)
 * const request = buildBurnRequest({
 *   amount: 50,
 *   contractAddress: '0x...',
 *   chainId: 137,
 *   engagedBusinessId: 'business-456',
 *   authorizedSubmitterId: 'business-456',
 *   authorizedSubmitterType: AccountOwnerType.BUSINESS
 * });
 * ```
 */
function buildBurnRequest(data) {
    return {
        web3TransactionType: Web3TransactionType.BURN,
        amount: Math.round(data.amount),
        token: buildTokenSelector(data),
        // Only include sender if an identifier is provided (for admin burns)
        ...(data.senderAccountId || data.senderAccountAddress) && {
            sender: buildAccountSelector({
                accountId: data.senderAccountId,
                accountType: data.senderAccountType || AccountOwnerType.USER,
                accountAddress: data.senderAccountAddress
            })
        },
        // POS authorization fields
        ...(data.engagedBusinessId && { engagedBusinessId: data.engagedBusinessId }),
        ...(data.authorizedSubmitterId && { authorizedSubmitterId: data.authorizedSubmitterId }),
        ...(data.authorizedSubmitterType && { authorizedSubmitterType: data.authorizedSubmitterType })
    };
}
/**
 * 🎯 Build a TRANSFER transaction request (business endpoint)
 * Transfers tokens between accounts
 *
 * @example Basic Transfer
 * ```typescript
 * const request = buildTransferRequest({
 *   amount: 25,
 *   contractAddress: '0x...',
 *   chainId: 137,
 *   senderAccountId: 'user-123',
 *   senderAccountType: AccountOwnerType.USER,
 *   recipientAccountId: 'business-456',
 *   recipientAccountType: AccountOwnerType.BUSINESS
 * });
 * ```
 *
 * @example POS Flow (Business submits on behalf of User)
 * ```typescript
 * const request = buildTransferRequest({
 *   amount: 25,
 *   contractAddress: '0x...',
 *   chainId: 137,
 *   senderAccountId: 'user-123',
 *   senderAccountType: AccountOwnerType.USER,
 *   recipientAccountId: 'business-456',
 *   recipientAccountType: AccountOwnerType.BUSINESS,
 *   // POS authorization - allows business to submit the signed transaction
 *   engagedBusinessId: 'business-456',
 *   authorizedSubmitterId: 'business-456',
 *   authorizedSubmitterType: AccountOwnerType.BUSINESS
 * });
 * ```
 */
function buildTransferRequest(data) {
    return {
        web3TransactionType: Web3TransactionType.TRANSFER,
        amount: Math.round(data.amount),
        token: buildTokenSelector(data),
        ...(data.senderAccountId || data.senderAccountType || data.senderAccountAddress) && {
            sender: buildAccountSelector({
                accountId: data.senderAccountId,
                accountType: data.senderAccountType,
                accountAddress: data.senderAccountAddress
            })
        },
        ...(data.recipientAccountId || data.recipientAccountType || data.recipientAccountAddress) && {
            recipient: buildAccountSelector({
                accountId: data.recipientAccountId,
                accountType: data.recipientAccountType,
                accountAddress: data.recipientAccountAddress
            })
        },
        // POS authorization fields
        ...(data.engagedBusinessId && { engagedBusinessId: data.engagedBusinessId }),
        ...(data.authorizedSubmitterId && { authorizedSubmitterId: data.authorizedSubmitterId }),
        ...(data.authorizedSubmitterType && { authorizedSubmitterType: data.authorizedSubmitterType })
    };
}
/**
 * 🎯 Build a transaction submission request from QR code parameters
 * Handles the logic of mapping signature to the correct field based on transaction format.
 *
 * @example
 * ```typescript
 * const request = buildSubmissionRequest({
 *   transactionId: 'tx-123',
 *   transactionFormat: 'EIP-712',
 *   signature: '0x...'
 * });
 * ```
 */
function buildSubmissionRequest(params) {
    return {
        transactionId: params.transactionId,
        type: params.transactionFormat,
        signature: params.transactionFormat === 'EIP-712' ? params.signature : undefined,
        signedTransaction: params.transactionFormat !== 'EIP-712' ? params.signature : undefined,
    };
}
/**
 * 🎯 Build a POS TRANSFER transaction request
 * Convenience function for POS (Point of Sale) scenarios where a business
 * submits a transaction on behalf of a user.
 *
 * This sets up the authorization fields correctly so the business can submit
 * the user-signed transaction.
 *
 * @example POS Flow
 * ```typescript
 * // Step 1: Create POS transaction (user to business)
 * const request = buildPOSTransferRequest({
 *   amount: 100,
 *   contractAddress: '0x...',
 *   chainId: 137,
 *   userId: 'user-123',
 *   businessId: 'business-456'
 * });
 *
 * // Step 2: Prepare and get signing data
 * const response = await sdk.transactions.prepareClientSignedTransaction(request);
 *
 * // Step 3: User signs the transaction
 * const signature = await userWallet.signTypedData(response.signingData);
 *
 * // Step 4: Business submits the signed transaction
 * await sdk.transactions.submitSignedTransaction({
 *   transactionId: response.transaction.id,
 *   type: 'EIP_712',
 *   signature
 * });
 * ```
 */
function buildPOSTransferRequest(data) {
    return buildTransferRequest({
        amount: data.amount,
        contractAddress: data.contractAddress,
        contractTokenId: data.contractTokenId,
        chainId: data.chainId,
        senderAccountId: data.userId,
        senderAccountType: AccountOwnerType.USER,
        senderAccountAddress: data.userAccountAddress,
        recipientAccountId: data.businessId,
        recipientAccountType: AccountOwnerType.BUSINESS,
        recipientAccountAddress: data.businessAccountAddress,
        // POS authorization - business is engaged and authorized to submit
        engagedBusinessId: data.businessId,
        authorizedSubmitterId: data.businessId,
        authorizedSubmitterType: AccountOwnerType.BUSINESS
    });
}
/**
 * 🎯 Build a POS BURN transaction request
 * Convenience function for POS scenarios where a business facilitates
 * a user burning tokens (e.g., redeeming rewards at point of sale).
 *
 * This sets up the authorization fields correctly so the business can submit
 * the user-signed burn transaction.
 *
 * @example POS Burn Flow
 * ```typescript
 * // Step 1: Create POS burn transaction
 * const request = buildPOSBurnRequest({
 *   amount: 1,
 *   contractAddress: '0x...',
 *   contractTokenId: '123', // NFT token ID
 *   chainId: 137,
 *   userId: 'user-123',
 *   businessId: 'business-456'
 * });
 *
 * // Step 2: Prepare and get signing data
 * const response = await sdk.transactions.prepareClientSignedTransaction(request);
 *
 * // Step 3: User signs the transaction
 * const signature = await userWallet.signTypedData(response.signingData);
 *
 * // Step 4: Business submits the signed transaction
 * await sdk.transactions.submitSignedTransaction({
 *   transactionId: response.transaction.id,
 *   type: 'EIP_712',
 *   signature
 * });
 * ```
 */
function buildPOSBurnRequest(data) {
    return buildBurnRequest({
        amount: data.amount,
        contractAddress: data.contractAddress,
        contractTokenId: data.contractTokenId,
        chainId: data.chainId,
        senderAccountId: data.userId,
        senderAccountType: AccountOwnerType.USER,
        senderAccountAddress: data.userAccountAddress,
        // POS authorization - business is engaged and authorized to submit
        engagedBusinessId: data.businessId,
        authorizedSubmitterId: data.businessId,
        authorizedSubmitterType: AccountOwnerType.BUSINESS
    });
}

export { ClientTransactionType as C, TransactionApi as T, TransactionService as a, buildMintRequest as b, buildBurnRequest as c, buildTransferRequest as d, buildPOSTransferRequest as e, buildPOSBurnRequest as f, buildSubmissionRequest as g, extractDeadlineFromSigningData as h, buildPendingTransactionData as i, needsSubmission as j, getSigningUrl as k, needsExternalSigning as n };
//# sourceMappingURL=transaction-request.builder-D6VLhaMi.js.map
